import type { JSX } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { FileText, Plus } from 'lucide-react';
import { Badge, type BadgeProps, Button } from '@caratuva/ui';
import type { InvoiceStatus } from '@caratuva/shared-types';
import { listInvoices } from '../lib/api';

/*
 * GetPaid invoice list. Simplified consumer table — no seller column, since
 * GetPaid always invoices as the org's own (self) account.
 */

export const Route = createFileRoute('/_app/invoices/')({
  component: InvoicesIndex,
});

const STATUS_VARIANT: Record<InvoiceStatus, BadgeProps['variant']> = {
  draft: 'secondary',
  awaiting_approval: 'warning',
  approved: 'info',
  awaiting_buyer: 'info',
  buyer_kyc_pending: 'warning',
  buyer_kyc_approved: 'info',
  buyer_kyc_rejected: 'danger',
  fx_quoted: 'info',
  fx_expired: 'warning',
  on_ramp_pending: 'warning',
  on_ramp_failed: 'danger',
  fiat_received: 'info',
  on_chain_confirmed: 'info',
  offramp_pending: 'warning',
  offramp_failed: 'danger',
  settled: 'success',
  partial_payment: 'warning',
  expired: 'secondary',
  cancelled: 'destructive',
  restored: 'info',
  manual_review: 'warning',
};

function formatDate(iso: string, locale: string): string {
  try {
    return new Date(iso).toLocaleDateString(locale, { dateStyle: 'medium' });
  } catch {
    return iso;
  }
}

function InvoicesIndex(): JSX.Element {
  const intl = useIntl();
  const query = useQuery({
    queryKey: ['invoices'],
    queryFn: ({ signal }) => listInvoices(signal),
  });

  return (
    <section className="mx-auto max-w-5xl">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-xl font-semibold tracking-tight text-navy-900">
          {intl.formatMessage({ id: 'invoices.title', defaultMessage: 'Invoices' })}
        </h1>
        <Button asChild>
          <Link to="/invoices/new">
            <Plus aria-hidden className="h-4 w-4" strokeWidth={1.5} />
            {intl.formatMessage({ id: 'invoices.new', defaultMessage: 'New invoice' })}
          </Link>
        </Button>
      </div>

      {query.isPending ? (
        <p className="text-sm text-navy-600">
          {intl.formatMessage({ id: 'invoices.loading', defaultMessage: 'Loading invoices...' })}
        </p>
      ) : query.isError ? (
        <p className="text-sm text-[var(--color-danger)]">
          {intl.formatMessage({
            id: 'invoices.error',
            defaultMessage: 'Failed to load invoices.',
          })}
        </p>
      ) : query.data.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-md border border-dashed border-navy-200 bg-white px-6 py-16 text-center">
          <FileText aria-hidden className="h-8 w-8 text-navy-500" strokeWidth={1.5} />
          <div>
            <p className="text-sm font-medium text-navy-900">
              {intl.formatMessage({
                id: 'invoices.empty.title',
                defaultMessage: 'No invoices yet',
              })}
            </p>
            <p className="mt-1 text-sm text-navy-600">
              {intl.formatMessage({
                id: 'invoices.empty.description',
                defaultMessage: 'Send your first invoice and start getting paid.',
              })}
            </p>
          </div>
          <Button asChild>
            <Link to="/invoices/new">
              <Plus aria-hidden className="h-4 w-4" strokeWidth={1.5} />
              {intl.formatMessage({
                id: 'invoices.empty.cta',
                defaultMessage: 'Create your first invoice',
              })}
            </Link>
          </Button>
        </div>
      ) : (
        <div className="overflow-hidden rounded-md border border-navy-200 bg-white">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-navy-200 bg-navy-50 text-xs uppercase tracking-wide text-navy-700">
              <tr>
                <th className="px-4 py-2">
                  {intl.formatMessage({ id: 'invoices.column.status', defaultMessage: 'Status' })}
                </th>
                <th className="px-4 py-2">
                  {intl.formatMessage({
                    id: 'invoices.column.buyer',
                    defaultMessage: 'Buyer',
                  })}
                </th>
                <th className="px-4 py-2">
                  {intl.formatMessage({ id: 'invoices.column.amount', defaultMessage: 'Amount' })}
                </th>
                <th className="px-4 py-2">
                  {intl.formatMessage({
                    id: 'invoices.column.createdAt',
                    defaultMessage: 'Created',
                  })}
                </th>
              </tr>
            </thead>
            <tbody>
              {query.data.map((invoice) => (
                <tr key={invoice.id} className="border-b border-navy-100 last:border-b-0">
                  <td className="px-4 py-2">
                    <Link to="/invoices/$id" params={{ id: invoice.id }} className="block">
                      <Badge
                        variant={STATUS_VARIANT[invoice.status as InvoiceStatus] ?? 'secondary'}
                        className="font-mono text-[11px] uppercase tracking-wider"
                      >
                        {invoice.status}
                      </Badge>
                    </Link>
                  </td>
                  <td className="px-4 py-2">
                    <Link to="/invoices/$id" params={{ id: invoice.id }} className="block">
                      {invoice.buyerName ? (
                        <span className="text-navy-900">{invoice.buyerName}</span>
                      ) : invoice.buyerEmail ? (
                        <span className="text-navy-700">{invoice.buyerEmail}</span>
                      ) : (
                        <span className="font-mono text-xs text-navy-700">{invoice.buyerId}</span>
                      )}
                    </Link>
                  </td>
                  <td className="px-4 py-2 tabular-nums">
                    <Link to="/invoices/$id" params={{ id: invoice.id }} className="block">
                      {invoice.amount} {invoice.currency}
                    </Link>
                  </td>
                  <td className="px-4 py-2 tabular-nums">
                    <Link to="/invoices/$id" params={{ id: invoice.id }} className="block">
                      {formatDate(invoice.createdAt, intl.locale)}
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
